import React, { useEffect, useState, useRef } from 'react';
import {
    Button,
    Space,
    Typography,
    Avatar,
    message,
    Spin,
    Alert,
    Modal,
    Select,
    Radio
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
    UserOutlined,
    ClockCircleOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import {
    useUpdateJoinStatus
} from '../hooks/useGraphQL';
import { useAppointmentAgoraLink } from '../hooks/useAgoraLink';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../hooks/useAuth';
import FloatingCaseNotes from '../components/FloatingCaseNotes';
import AudioTestModal from '../components/VideoCall/AudioTestModal';
import CallControls from '../components/VideoCall/CallControls';
import CallHeader from '../components/VideoCall/CallHeader';
import StartCallButton from '../components/VideoCall/StartCallButton';
import dayjs from 'dayjs';
import AgoraRTC, {
    IAgoraRTCClient,
    IAgoraRTCRemoteUser,
    ILocalVideoTrack,
    ILocalAudioTrack,
    RemoteStreamType,
    DeviceInfo
} from 'agora-rtc-sdk-ng';
import { useGetAppointmentQuery } from '../generated/graphql';
import { FIXED_VIDEO_HEIGHT } from 'constants/styles';
import { CameraOff, Camera } from 'lucide-react';

// iOS detection utility
const isIPhone = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    const userAgent = window.navigator.userAgent;
    const isIPhone = /iPhone/i.test(userAgent);
    const isIPad = /iPad/i.test(userAgent);
    const isIPod = /iPod/i.test(userAgent);
    const isIOS = /iOS/i.test(userAgent) ||
        (/Macintosh/i.test(userAgent) && 'ontouchend' in document);
    return isIPhone || isIPad || isIPod || isIOS;
};

const { Title, Text } = Typography;

interface PatientProfile {
    id: string;
    first_name_en: string;
    last_name_en: string;
    first_name_zh_hk?: string;
    last_name_zh_hk?: string;
}

const getPatientDisplayName = (patient?: PatientProfile): string => {
    if (!patient) return '';
    const englishName = `${patient.first_name_en || ''} ${patient.last_name_en || ''}`.trim();
    const chineseName = `${patient.first_name_zh_hk || ''} ${patient.last_name_zh_hk || ''}`.trim();
    if (englishName && chineseName) return `${englishName} (${chineseName})`;
    return englishName || chineseName || '';
};

// Track who is displayed in the main view
// type: 'local' shows self-camera; 'remote' shows a selected remote user's video
interface MainViewState {
    type: 'local' | 'remote';
    remoteUid?: string | number | null;
}

const AgoraCall: React.FC = () => {
    const { t } = useTranslation();
    const { appointmentId } = useParams<{ appointmentId: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const [isInCall, setIsInCall] = useState(false);
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [finalCallDuration, setFinalCallDuration] = useState(0);
    const [showEndCallModal, setShowEndCallModal] = useState(false);
    const [showAudioDeviceDialog, setShowAudioDeviceDialog] = useState(false);
    const [showAutoplayBlockedDialog, setShowAutoplayBlockedDialog] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isReconnectingAudio, setIsReconnectingAudio] = useState(false);
    const [dialogSelectedMicrophone, setDialogSelectedMicrophone] = useState<string>('');
    const [showStartCallButton, setShowStartCallButton] = useState(true);
    const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
    const [localTracks, setLocalTracks] = useState<{
        audioTrack?: ILocalAudioTrack;
        videoTrack?: ILocalVideoTrack;
    }>({});

    // Audio device state
    const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([]);
    const [availableSpeakers, setAvailableSpeakers] = useState<MediaDeviceInfo[]>([]);
    const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
    const [selectedSpeaker, setSelectedSpeaker] = useState<string>('');
    const [audioDeviceError, setAudioDeviceError] = useState<string>('');
    const [audioLevel, setAudioLevel] = useState<number>(0);
    const [showAudioTest, setShowAudioTest] = useState(false);
    const [isTestingAudio, setIsTestingAudio] = useState(false);
    const audioLevelIntervalRef = useRef<NodeJS.Timeout>();
    const [audioReconnectAttempts, setAudioReconnectAttempts] = useState(0);
    const [networkQuality, setNetworkQuality] = useState<'excellent' | 'good' | 'poor' | 'bad' | 'very bad' | 'down'>('excellent');

    // Microphone test state
    const [showMicrophoneTest, setShowMicrophoneTest] = useState(false);

    // Case notes state
    const [showCaseNotes, setShowCaseNotes] = useState(false);
    const [caseNotesPosition, setCaseNotesPosition] = useState({ x: 20, y: 100 });

    // Manage which stream is in the main view (default to remote if someone joins, otherwise local)
    const [mainView, setMainView] = useState<MainViewState>({ type: 'local' });
    const lastRemoteMainUidRef = useRef<string | number | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);
    const localVideoRef = useRef<HTMLVideoElement>(null);
    const remoteVideoRefs = useRef<Record<string | number, HTMLVideoElement | null>>({});
    // Store container refs for remote thumbnail videos
    const remoteThumbRefs = useRef<Record<string | number, HTMLDivElement | null>>({});
    const callTimerRef = useRef<NodeJS.Timeout>();
    const agoraClientRef = useRef<IAgoraRTCClient | null>(null);
    const autoplayDialogShownRef = useRef<number>(0); // Timestamp when autoplay dialog was last shown

    // Thumbnail minimized state
    const [minimizedThumbnails, setMinimizedThumbnails] = useState<Set<string | number>>(new Set());

    // Fetch appointment data
    const { data: appointmentData, loading: appointmentLoading } = useGetAppointmentQuery({
        variables: { id: appointmentId || '' },
        skip: !appointmentId,
    });

    // Fetch Agora link - but don't auto-start
    const { agoraLink, loading: agoraLoading, error: agoraError, refetch: refetchAgoraLink } = useAppointmentAgoraLink(appointmentId || '');

    // Join status mutation hook
    const [updateJoinStatus] = useUpdateJoinStatus();

    const appointment = appointmentData?.appointment;

    const patientName: string = (() => {
        // First, try to find patient by patient_id
        if (appointment?.patient_id && appointment?.app_user?.patients) {
            const patient = appointment.app_user.patients.find(p => p.id === appointment.patient_id);
            if (patient) {
                return getPatientDisplayName(patient);
            }
        }
        // Fallback to first patient if available
        if (appointment?.app_user?.patients && appointment.app_user.patients.length > 0) {
            return getPatientDisplayName(appointment.app_user.patients[0]);
        }
        // Fallback to app_user name
        return appointment?.app_user ? `${appointment.app_user.firstname} ${appointment.app_user.lastname}`.trim() : '';
    })();

    // Initialize Agora client
    useEffect(() => {
        const handleMicrophoneChanged = (info: DeviceInfo) => {
            console.log("====== Microphone changed:", info.state, info.device);
            if (info.state === 'INACTIVE') {
                setAudioDeviceError('Microphone is not active. Please check your microphone permissions and try again.');
                setShowAudioDeviceDialog(true);
            }
        };
        const handlePlaybackDeviceChanged = (info: DeviceInfo) => {
            console.log("====== Playback device changed:", info.state, info.device);
        };
        const handleCameraChanged = (info: DeviceInfo) => {
            console.log("====== Camera changed:", info.state, info.device);
        };
        const handleAudioContextStateChanged = (currState: AudioContextState | "interrupted", prevState: AudioContextState | "interrupted" | undefined) => {
            console.log("====== Audio context state changed:", currState, prevState);
        };
        const handleAutoplayFailed = () => {
            console.error("====== Audio autoplay failed:");
            // Show dialog when autoplay fails
            const now = Date.now();
            if (now - autoplayDialogShownRef.current > 5000) {
                console.log(`[AUTOPLAY DIALOG] Showing autoplay blocked dialog (onAutoplayFailed event)`);
                autoplayDialogShownRef.current = now;
                setShowAutoplayBlockedDialog(true);
            }
        };
        AgoraRTC.on('microphone-changed', handleMicrophoneChanged);
        AgoraRTC.on('playback-device-changed', handlePlaybackDeviceChanged);
        AgoraRTC.on('camera-changed', handleCameraChanged);
        AgoraRTC.on('audio-context-state-changed', handleAudioContextStateChanged);
        AgoraRTC.on('autoplay-failed', handleAutoplayFailed);
        if (!agoraClientRef.current) {
            agoraClientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        }

        return () => {
            AgoraRTC.off('microphone-changed', handleMicrophoneChanged);
            AgoraRTC.off('playback-device-changed', handlePlaybackDeviceChanged);
            AgoraRTC.off('camera-changed', handleCameraChanged);
            AgoraRTC.off('audio-context-state-changed', handleAudioContextStateChanged);
            AgoraRTC.off('autoplay-failed', handleAutoplayFailed);
            if (agoraClientRef.current) {
                agoraClientRef.current.leave();
            }
        };
    }, []);

    // Prevent scrolling on the call page
    useEffect(() => {
        // Add class to html and body to prevent scrolling
        document.documentElement.classList.add('call-page-active');
        document.body.classList.add('call-page-active');

        // Cleanup function to remove the class when component unmounts
        return () => {
            document.documentElement.classList.remove('call-page-active');
            document.body.classList.remove('call-page-active');
        };
    }, []);

    const smv = React.useCallback((uid: string | number) => {
        setTimeout(() => {
            setMainView({ type: 'remote', remoteUid: uid });
        }, 500);
        lastRemoteMainUidRef.current = uid;
    }, []);

    // Audio device detection and validation
    const detectAudioDevices = async () => {
        try {
            setAudioDeviceError('');

            // Request permissions first
            await navigator.mediaDevices.getUserMedia({ audio: true });

            // Get available microphones
            const devices = await navigator.mediaDevices.enumerateDevices();
            const microphones = devices.filter(device => device.kind === 'audioinput');
            let speakers = devices.filter(device => device.kind === 'audiooutput');

            // iOS-specific speaker handling
            if (isIPhone() && speakers.length === 0) {
                console.log('iOS detected with no enumerated speakers - creating virtual speaker entries');
                // On iOS, speakers are often not enumerated due to privacy restrictions
                // Create virtual speaker entries for iOS devices
                speakers = [
                    {
                        deviceId: 'default',
                        groupId: 'default',
                        kind: 'audiooutput' as MediaDeviceKind,
                        label: 'Default Speaker',
                        toJSON: () => ({ deviceId: 'default', groupId: 'default', kind: 'audiooutput', label: 'Default Speaker' })
                    } as MediaDeviceInfo,
                    {
                        deviceId: 'speaker',
                        groupId: 'speaker',
                        kind: 'audiooutput' as MediaDeviceKind,
                        label: 'Speaker (External)',
                        toJSON: () => ({ deviceId: 'speaker', groupId: 'speaker', kind: 'audiooutput', label: 'Speaker (External)' })
                    } as MediaDeviceInfo
                ];
            }

            setAvailableMicrophones(microphones);
            setAvailableSpeakers(speakers);

            // Set default devices
            if (microphones.length > 0 && !selectedMicrophone) {
                setSelectedMicrophone(microphones[0].deviceId);
            }
            if (speakers.length > 0 && !selectedSpeaker) {
                setSelectedSpeaker(speakers[0].deviceId);
            }

            // Test microphone access
            if (microphones.length === 0) {
                const errorMessage = 'No microphones detected. Please connect a microphone.';
                setAudioDeviceError(errorMessage);
                setShowAudioDeviceDialog(true);
                return false;
            }

            return true;
        } catch (error: any) {
            console.error('Audio device detection failed:', error);
            let errorMessage = '';
            if (error.name === 'NotAllowedError') {
                errorMessage = 'Microphone permission denied. Please allow microphone access.';
            } else if (error.name === 'NotFoundError') {
                errorMessage = 'No microphone found. Please connect a microphone.';
            } else {
                errorMessage = `Audio device error: ${error.message}`;
            }
            setAudioDeviceError(errorMessage);
            // Show dialog when detection fails
            setShowAudioDeviceDialog(true);
            return false;
        }
    };

    // Handle audio device reconnection
    const handleReconnectAudio = async () => {
        if (!dialogSelectedMicrophone && availableMicrophones.length === 0) {
            message.warning('Please select a microphone or wait for devices to be detected.');
            return;
        }

        setIsReconnectingAudio(true);
        setAudioDeviceError('');
        
        try {
            // First, refresh device list
            const success = await detectAudioDevices();
            
            // Use the microphone selected in the dialog, or fallback to first available
            const microphoneToUse = dialogSelectedMicrophone || (availableMicrophones.length > 0 ? availableMicrophones[0].deviceId : selectedMicrophone);
            
            if (!microphoneToUse) {
                setAudioDeviceError('No microphone selected. Please select a microphone and try again.');
                setIsReconnectingAudio(false);
                return;
            }

            // Update selected microphone
            setSelectedMicrophone(microphoneToUse);

            if (success || availableMicrophones.length > 0) {
                // Successfully detected devices, dialog will not show again
                console.log('Audio device detection successful after reconnect');
                
                // If user is already in the call, create and publish audio track
                if (isInCall && agoraClientRef.current) {
                    try {
                        // Close existing audio track if any
                        if (localTracks.audioTrack) {
                            localTracks.audioTrack.close();
                        }

                        const audioConfig: any = {
                            encoderConfig: {
                                sampleRate: 48000,
                                stereo: false,
                                bitrate: 64
                            },
                            processing: true,
                            latency: 'interactive'
                        };
                        if (microphoneToUse) {
                            audioConfig.microphoneId = microphoneToUse;
                        }
                        
                        const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);
                        console.log('Audio track created after reconnect with microphone:', microphoneToUse);
                        
                        // Set up event listeners for the new track
                        newAudioTrack.on('transceiver-updated', (transceiver: RTCRtpTransceiver, type) => {
                            console.log("====== Transceiver updated:", transceiver, type);
                        });
                        newAudioTrack.on('track-updated', (track: MediaStreamTrack) => {
                            console.log("====== Track updated:", track);
                        });
                        newAudioTrack.on('track-ended', () => {
                            console.log("====== Track ended:");
                            setAudioDeviceError('Audio track ended. Please reconnect.');
                            setShowAudioDeviceDialog(true);
                        });
                        
                        // Update local tracks
                        setLocalTracks(prev => ({ ...prev, audioTrack: newAudioTrack }));
                        
                        // Publish the audio track
                        await agoraClientRef.current.publish([newAudioTrack]);
                        console.log('Audio track published after reconnect');
                        
                        // Close dialog on success
                        setShowAudioDeviceDialog(false);
                        message.success('Audio reconnected successfully');
                    } catch (audioError: any) {
                        console.error('Failed to create audio track after reconnect:', audioError);
                        const errorMessage = `Failed to create audio track: ${audioError.message}`;
                        setAudioDeviceError(errorMessage);
                        // Keep dialog open so user can try again
                    }
                } else {
                    // Not in call yet, just close dialog
                    setShowAudioDeviceDialog(false);
                    message.success('Audio device ready');
                }
            } else {
                // If still fails, dialog will remain open
                console.error('Audio device detection still failed after reconnect');
            }
        } catch (error: any) {
            console.error('Error reconnecting audio devices:', error);
            setAudioDeviceError(`Reconnection failed: ${error.message}`);
            // Keep dialog open so user can try again
        } finally {
            setIsReconnectingAudio(false);
        }
    };

    // Refresh available microphones when dialog opens
    useEffect(() => {
        if (showAudioDeviceDialog) {
            // Refresh device list when dialog opens
            detectAudioDevices();
        }
    }, [showAudioDeviceDialog]);

    // Set default microphone selection when dialog opens or devices change
    useEffect(() => {
        if (showAudioDeviceDialog && !dialogSelectedMicrophone) {
            if (availableMicrophones.length > 0) {
                setDialogSelectedMicrophone(availableMicrophones[0].deviceId);
            } else if (selectedMicrophone) {
                // Use the previously selected microphone if available
                setDialogSelectedMicrophone(selectedMicrophone);
            }
        }
    }, [showAudioDeviceDialog, availableMicrophones.length, dialogSelectedMicrophone, selectedMicrophone]);

    // Test audio device functionality
    const testAudioDevice = async (deviceId: string) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: { exact: deviceId } }
            });

            // Test if we can get audio data
            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            source.connect(analyser);

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);

            // Stop the test stream
            stream.getTracks().forEach(track => track.stop());
            audioContext.close();

            return true;
        } catch (error) {
            console.error('Audio device test failed:', error);
            return false;
        }
    };

    // Audio level monitoring
    // const startAudioLevelMonitoring = () => {
    //     if (audioLevelIntervalRef.current) {
    //         clearInterval(audioLevelIntervalRef.current);
    //     }

    //     audioLevelIntervalRef.current = setInterval(async () => {
    //         try {
    //             if (localTracks.audioTrack && !isAudioMuted) {
    //                 // Get the media stream from the audio track
    //                 const stream = localTracks.audioTrack.getMediaStreamTrack();
    //                 if (stream) {
    //                     const audioContext = new AudioContext();
    //                     const source = audioContext.createMediaStreamSource(new MediaStream([stream]));
    //                     const analyser = audioContext.createAnalyser();
    //                     analyser.fftSize = 256;
    //                     source.connect(analyser);

    //                     const dataArray = new Uint8Array(analyser.frequencyBinCount);
    //                     analyser.getByteFrequencyData(dataArray);

    //                     // Calculate average volume
    //                     const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
    //                     setAudioLevel(average / 255); // Normalize to 0-1

    //                     audioContext.close();
    //                 }
    //             } else {
    //                 setAudioLevel(0);
    //             }
    //         } catch (error) {
    //             // Silently handle errors to avoid spam
    //             setAudioLevel(0);
    //         }
    //     }, 100); // Update every 100ms
    // };

    // const stopAudioLevelMonitoring = () => {
    //     if (audioLevelIntervalRef.current) {
    //         clearInterval(audioLevelIntervalRef.current);
    //         audioLevelIntervalRef.current = undefined;
    //     }
    // };

    // Audio recovery and reconnection logic
    const recoverAudioTrack = async () => {
        if (audioReconnectAttempts >= 3) {
            setAudioDeviceError('Audio recovery failed after multiple attempts. Please check your microphone and try refreshing the page.');
            return false;
        }

        try {
            setAudioReconnectAttempts(prev => prev + 1);
            setAudioDeviceError('Attempting to recover audio...');

            // Close existing audio track if any
            if (localTracks.audioTrack) {
                localTracks.audioTrack.close();
            }

            // Try to recreate audio track with optimized settings
            const audioConfig: any = {
                encoderConfig: {
                    sampleRate: 48000,
                    stereo: false,
                    bitrate: 64
                },
                processing: true,
                latency: 'interactive'
            };

            if (selectedMicrophone) {
                audioConfig.microphoneId = selectedMicrophone;
            }

            const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);

            // Update local tracks
            setLocalTracks(prev => ({ ...prev, audioTrack: newAudioTrack }));

            // Publish the new track
            if (agoraClientRef.current) {
                await agoraClientRef.current.publish([newAudioTrack]);
            }

            // // Start monitoring again
            // startAudioLevelMonitoring();

            setAudioDeviceError('');
            setAudioReconnectAttempts(0);
            // message.success('Audio recovered successfully!');
            return true;

        } catch (error: any) {
            console.error('Audio recovery failed:', error);
            setAudioDeviceError(`Audio recovery failed: ${error.message}. Attempt ${audioReconnectAttempts}/3`);

            // Retry after a delay
            if (audioReconnectAttempts < 3) {
                setTimeout(() => {
                    recoverAudioTrack();
                }, 2000);
            }
            return false;
        }
    };


    // Comprehensive audio test with visual feedback
    const runAudioTest = async () => {
        setIsTestingAudio(true);
        setAudioDeviceError('');

        try {
            // Step 1: Check permissions
            await navigator.mediaDevices.getUserMedia({ audio: true });

            // Step 2: Test microphone
            if (selectedMicrophone) {
                const micTest = await testAudioDevice(selectedMicrophone);
                if (!micTest) {
                    setAudioDeviceError('Microphone test failed. Please check your microphone connection.');
                    return;
                }
            }

            // Step 3: Test audio output (speaker)
            try {
                const audioContext = new AudioContext();
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

                oscillator.start();

                // Play test tone for 1 second
                setTimeout(() => {
                    oscillator.stop();
                    audioContext.close();
                    message.success('Audio test completed successfully!');
                }, 1000);

            } catch (speakerError) {
                console.warn('Speaker test failed:', speakerError);
                message.warning('Microphone test passed, but speaker test failed. You may not hear others.');
            }

        } catch (error: any) {
            console.error('Audio test failed:', error);
            if (error.name === 'NotAllowedError') {
                setAudioDeviceError('Microphone permission denied. Please allow microphone access in your browser settings.');
            } else if (error.name === 'NotFoundError') {
                setAudioDeviceError('No microphone found. Please connect a microphone and try again.');
            } else {
                setAudioDeviceError(`Audio test failed: ${error.message}`);
            }
        } finally {
            setIsTestingAudio(false);
        }
    };

    // Load memorized device settings
    const loadMemorizedDeviceSettings = () => {
        const savedMic = localStorage.getItem('audioTest_microphone');
        const savedSpeaker = localStorage.getItem('audioTest_speaker');

        if (savedMic) {
            setSelectedMicrophone(savedMic);
        }
        if (savedSpeaker) {
            setSelectedSpeaker(savedSpeaker);
        }
    };

    // Save device settings to memory
    const saveDeviceSettings = (microphoneId: string, speakerId: string) => {
        localStorage.setItem('audioTest_microphone', microphoneId);
        localStorage.setItem('audioTest_speaker', speakerId);
    };

    // Detect audio devices on component mount
    useEffect(() => {
        detectAudioDevices();
        loadMemorizedDeviceSettings();
    }, []);

    // Save device settings when they change
    useEffect(() => {
        if (selectedMicrophone && selectedSpeaker) {
            saveDeviceSettings(selectedMicrophone, selectedSpeaker);
        }
    }, [selectedMicrophone, selectedSpeaker]);

    // // Monitor for audio issues and attempt recovery
    // useEffect(() => {
    //     if (isInCall && localTracks.audioTrack && !isAudioMuted) {
    //         let consecutiveLowAudioCount = 0;
    //         const maxConsecutiveChecks = 6; // 30 seconds (6 * 5 seconds)
    //         let lastRecoveryAttempt = 0;
    //         const recoveryCooldown = 30000; // 30 seconds cooldown between recovery attempts

    //         // Check if audio level is consistently low (potential issue)
    //         const checkAudioHealth = setInterval(() => {
    //             const now = Date.now();
    //             console.log("====== Audio level:", audioLevel);

    //             if (audioLevel === 0 && !isAudioMuted && localTracks.audioTrack) {
    //                 consecutiveLowAudioCount++;

    //                 // Only attempt recovery if:
    //                 // 1. We've had low audio for a significant time (30 seconds)
    //                 // 2. We haven't attempted recovery recently (30 second cooldown)
    //                 if (consecutiveLowAudioCount >= maxConsecutiveChecks &&
    //                     (now - lastRecoveryAttempt) > recoveryCooldown) {
    //                     console.warn(`Audio level has been 0 for ${consecutiveLowAudioCount * 5} seconds, attempting recovery...`);
    //                     lastRecoveryAttempt = now;
    //                     recoverAudioTrack();
    //                     consecutiveLowAudioCount = 0; // Reset counter after recovery attempt
    //                 }
    //             } else {
    //                 // Reset counter if audio level is not 0
    //                 consecutiveLowAudioCount = 0;
    //             }
    //         }, 5000); // Check every 5 seconds

    //         return () => clearInterval(checkAudioHealth);
    //     }
    // }, [isInCall, localTracks.audioTrack, isAudioMuted, audioLevel]);

    // Set speaker for Agora when selected speaker changes
    useEffect(() => {
        if (selectedSpeaker && remoteUsers.length > 0) {
            setAgoraSpeaker(selectedSpeaker);
        }
    }, [selectedSpeaker, remoteUsers]);

    // Set up event listeners for Agora client
    useEffect(() => {
        if (!agoraClientRef.current) return;

        const client = agoraClientRef.current;

        const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
            console.log("====== User published:", user, mediaType);
            await client.subscribe(user, mediaType);

            if (mediaType === 'video') {
                setRemoteUsers(prev => {
                    const existing = prev.find(u => u.uid === user.uid);
                    const next = existing ? prev.map(u => u.uid === user.uid ? user : u) : [...prev, user];
                    console.log("====== Setting remote users:", next, mainView.type);

                    // Always prioritize remote users in main view when they publish video
                    // If this is the first remote user with video, make them the main view
                    if (next.length > 0) {
                        smv(user.uid);
                    }
                    return next;
                });
            } else if (mediaType === 'audio') {
                // Play remote audio so we can hear other participants
                user.audioTrack?.play();

                // Set speaker for this remote audio track if a speaker is selected
                if (selectedSpeaker && user.audioTrack) {
                    try {
                        user.audioTrack.setPlaybackDevice(selectedSpeaker);
                    } catch (error) {
                        console.warn('Could not set playback device for remote audio:', error);
                    }
                }
            }
        };

        const handleUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType?: 'audio' | 'video') => {
            console.log("====== User unpublished:", user, mediaType);
            if (mediaType === 'audio') {
                user.audioTrack?.stop();
            }
            if (mediaType === 'video' || mediaType === undefined) {
                setRemoteUsers(prev => {
                    const next = prev.filter(u => u.uid !== user.uid);
                    // If the main view was this user, fall back to local or another remote
                    if (mainView.type === 'remote' && mainView.remoteUid === user.uid) {
                        const fallback = next[0];
                        if (fallback) {
                            setMainView({ type: 'remote', remoteUid: fallback.uid });
                            lastRemoteMainUidRef.current = fallback.uid;
                        } else {
                            setMainView({ type: 'local', remoteUid: null });
                        }
                    }
                    return next;
                });
            }
        };

        const handleUserJoined = (user: IAgoraRTCRemoteUser) => {
            console.log("====== User joined:", user);
            setRemoteUsers(prev => [...prev, user]);
        };

        const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
            console.log("====== User left:", user);
            setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
            if (mainView.type === 'remote' && mainView.remoteUid === user.uid) {
                const others = remoteUsers.filter(u => u.uid !== user.uid);
                const fallback = others[0];
                if (fallback) {
                    setMainView({ type: 'remote', remoteUid: fallback.uid });
                    lastRemoteMainUidRef.current = fallback.uid;
                } else {
                    setMainView({ type: 'local', remoteUid: null });
                }
            }
        };

        // Network quality monitoring
        const handleNetworkQuality = (stats: any) => {
            console.log("====== Network quality:", stats);
            const quality = stats.uplinkNetworkQuality || stats.downlinkNetworkQuality;
            if (quality !== undefined) {
                const qualityLevels = ['excellent', 'good', 'poor', 'bad', 'very bad', 'down'];
                setNetworkQuality(qualityLevels[quality] as any);

                // Log network quality for debugging
                if (quality > 2) {
                    console.warn(`Network quality degraded: ${qualityLevels[quality]} (${quality})`);
                }
            }
        };

        const connectionStateChangeHandler = (newState: string, oldState: string) => {
            console.log("====== Connection state changed:", newState, oldState);
        };
        const exceptionHandler = (error: any) => {
            console.log("====== Exception:", error);
        };

        client.on('user-published', handleUserPublished);
        client.on('user-unpublished', handleUserUnpublished as any);
        client.on('user-joined', handleUserJoined);
        client.on('user-left', handleUserLeft);
        client.on('network-quality', handleNetworkQuality);
        client.on('connection-state-change', connectionStateChangeHandler);
        client.on('exception', exceptionHandler);

        return () => {
            client.off('user-published', handleUserPublished);
            client.off('user-unpublished', handleUserUnpublished as any);
            client.off('user-joined', handleUserJoined);
            client.off('user-left', handleUserLeft);
            client.off('network-quality', handleNetworkQuality);
            client.off('connection-state-change', connectionStateChangeHandler);
            client.off('exception', exceptionHandler);
        };
    }, [mainView, remoteUsers, smv]);

    // Handle main video stream playback (switch between local and selected remote)
    // This effect automatically switches the main view to show remote users when they join
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return;

        if (mainView.type === 'local') {
            if (localTracks.videoTrack) {
                localTracks.videoTrack.play(videoEl);
            } else {
                // No local video track available
            }
        } else if (mainView.type === 'remote' && mainView.remoteUid != null) {
            const remote = remoteUsers.find(u => u.uid === mainView.remoteUid);
            if (remote?.videoTrack) {
                remote.videoTrack.play(videoEl);
                setTimeout(() => {
                    remote.videoTrack?.play(videoEl);
                }, 100);
            } else {
                // No remote video track available for uid
            }
        }
    }, [mainView, localTracks.videoTrack, remoteUsers]);

    // Handle remote thumbnail videos playback (excluding the one shown in main view)
    useEffect(() => {
        remoteUsers.forEach(u => {
            // Skip playing in thumbnail if this user is displayed in main view
            const isMain = mainView.type === 'remote' && mainView.remoteUid === u.uid;
            const container = remoteThumbRefs.current[u.uid];
            if (!isMain && container && u.videoTrack) {
                try {
                    u.videoTrack.play(container);
                } catch (e) {
                    // no-op; play may throw if container not attached yet
                }
            }
        });
    }, [remoteUsers, mainView]);

    // Handle local PiP video stream playback when local is NOT in main view
    useEffect(() => {
        if (localTracks.videoTrack && localVideoRef.current && !(mainView.type === 'local')) {
            localTracks.videoTrack.play(localVideoRef.current);
        }
    }, [localTracks.videoTrack, mainView]);

    // Additional effect to ensure local video plays in thumbnail when available
    useEffect(() => {
        if (localTracks.videoTrack && localVideoRef.current && !isVideoOff) {
            localTracks.videoTrack.play(localVideoRef.current);
        }
    }, [localTracks.videoTrack, isVideoOff]);

    useEffect(() => {
        if (isInCall) {
            callTimerRef.current = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }

        return () => {
            if (callTimerRef.current) {
                clearInterval(callTimerRef.current);
            }
        };
    }, [isInCall]);

    const toggleThumbnailMinimize = (uid: string | number | 'local') => {
        setMinimizedThumbnails(prev => {
            const newSet = new Set(prev);
            if (newSet.has(uid)) {
                newSet.delete(uid);
            } else {
                newSet.add(uid);
            }
            return newSet;
        });
    };

    const handleStartCall = async () => {
        if (!agoraClientRef.current) return;

        if (!currentUser) {
            message.error(t('auth.notAuthenticated'));
            return;
        }

        setIsConnecting(true);
        setShowStartCallButton(false);

        try {
            // First, generate/fetch the Agora link from backend
            const { data } = await refetchAgoraLink();
            const agoraLinkData = data?.getAppointmentAgoraLink;

            if (!agoraLinkData) {
                throw new Error('Failed to generate Agora link');
            }

            const client = agoraClientRef.current;

            // Parse the Agora link to extract channel info
            const appId = agoraLinkData.appId;
            const channelName = agoraLinkData.channelName
            const uid = `${currentUser.doctor_id ? 'doctor-' : ''}${currentUser.id}`
            const role = agoraLinkData.role
            const token = agoraLinkData.token

            // Join the channel
            await client.join(appId, channelName, token, uid);

            // Create local tracks with enhanced audio device handling
            let audioTrack: ILocalAudioTrack | undefined;
            let videoTrack: ILocalVideoTrack | undefined;

            // Enhanced audio track creation with device selection
            try {
                // First, ensure we have detected audio devices
                const audioDevicesReady = await detectAudioDevices();

                if (audioDevicesReady && availableMicrophones.length > 0) {
                    try {
                        // Create audio track with specific device if selected and optimized settings
                        const audioConfig: any = {
                            // Optimize audio quality settings
                            encoderConfig: {
                                sampleRate: 48000, // Higher sample rate for better quality
                                stereo: false, // Mono for better bandwidth usage
                                bitrate: 64 // Optimize bitrate for real-time communication
                            },
                            // Enable audio processing for better quality
                            processing: true,
                            // Optimize for low latency
                            latency: 'interactive'
                        };

                        if (selectedMicrophone) {
                            audioConfig.microphoneId = selectedMicrophone;
                        }

                        audioTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);
                        audioTrack.on('transceiver-updated', (transceiver: RTCRtpTransceiver, type) => {
                            console.log("====== Transceiver updated:", transceiver, type);
                        });
                        audioTrack.on('track-updated', (track: MediaStreamTrack) => {
                            console.log("====== Track updated:", track);
                        });
                        audioTrack.on('track-ended', () => {
                            console.log("====== Track ended:");
                        });
                        // // Set up audio level monitoring using Web Audio API
                        // if (audioTrack) {
                        //     startAudioLevelMonitoring();
                        // }

                        console.log('Audio track created successfully');
                    } catch (e: any) {
                        console.error('Failed to create microphone track:', e);

                        // Try fallback without specific device but with optimized settings
                        try {
                            const fallbackAudioConfig: any = {
                                encoderConfig: {
                                    sampleRate: 48000,
                                    stereo: false,
                                    bitrate: 64
                                },
                                processing: true,
                                latency: 'interactive'
                            };
                            audioTrack = await AgoraRTC.createMicrophoneAudioTrack(fallbackAudioConfig);
                            console.log('Audio track created with fallback method');
                        } catch (fallbackError: any) {
                            console.error('Fallback audio track creation failed:', fallbackError);
                            const errorMessage = `Microphone access failed: ${fallbackError.message}`;
                            setAudioDeviceError(errorMessage);
                            setShowAudioDeviceDialog(true);
                            message.error('Microphone not accessible. Please check your microphone permissions and try again.');
                        }
                    }
                } else {
                    const errorMessage = 'No microphones available. Please connect a microphone.';
                    setAudioDeviceError(errorMessage);
                    setShowAudioDeviceDialog(true);
                    message.warning('No microphone detected. Joining without microphone.');
                }
            } catch (e: any) {
                console.error('Error during audio setup:', e);
                const errorMessage = `Audio setup error: ${e.message}`;
                setAudioDeviceError(errorMessage);
                setShowAudioDeviceDialog(true);
                message.error('Audio setup failed. Please check your microphone and try again.');
            }

            // If audio track is none and we have an error, show dialog
            if (!audioTrack && audioDeviceError) {
                setShowAudioDeviceDialog(true);
            }

            try {
                const cams = await AgoraRTC.getCameras();
                if (cams && cams.length > 0) {
                    try {
                        videoTrack = await AgoraRTC.createCameraVideoTrack();
                    } catch (e: any) {
                        console.warn('Failed to create camera track:', e);
                        message.warning('Camera not accessible. Joining without camera.');
                    }
                } else {
                    message.warning('No camera detected. Joining without camera.');
                }
            } catch (e) {
                console.warn('Error while enumerating cameras:', e);
            }

            if (!audioTrack && !videoTrack) {
                message.warning('No input devices found. You have joined without microphone and camera.');
            }

            if (audioTrack || videoTrack) {
                setLocalTracks({ audioTrack, videoTrack });
                const toPublish = [audioTrack, videoTrack].filter(Boolean) as Array<ILocalAudioTrack | ILocalVideoTrack>;
                if (toPublish.length > 0) {
                    await client.publish(toPublish);
                }
            }

            // Default main view to local when user joins, will switch to remote when someone joins
            setMainView({ type: 'local', remoteUid: null });

            setIsInCall(true);
            setCallDuration(0);
            setFinalCallDuration(0);

            // Update join status
            if (appointmentId) {
                try {
                    await updateJoinStatus({
                        variables: {
                            id: appointmentId,
                            status: 'joined'
                        }
                    });
                } catch (error) {
                    console.error('Failed to update join status:', error);
                    // Don't fail the call if status update fails
                }
            }

            setIsConnecting(false);
            message.success(t('agora.callStarted'));

        } catch (error) {
            console.error('Failed to start call:', error);
            setIsConnecting(false);
            setShowStartCallButton(true);
            message.error(t('agora.callStartFailed'));
        }
    };

    const handleEndCall = () => {
        setFinalCallDuration(callDuration); // Store current duration before showing modal
        setShowEndCallModal(true);
    };

    const confirmEndCall = async () => {
        try {
            // Stop audio level monitoring
            // stopAudioLevelMonitoring();

            // Unpublish and close local tracks
            if (localTracks.audioTrack) {
                localTracks.audioTrack.close();
            }
            if (localTracks.videoTrack) {
                localTracks.videoTrack.close();
            }
            setLocalTracks({});

            // Leave the channel
            if (agoraClientRef.current) {
                await agoraClientRef.current.leave();
            }

            // Update join status to ended
            if (appointmentId) {
                try {
                    await updateJoinStatus({
                        variables: {
                            id: appointmentId,
                            status: 'ended'
                        }
                    });
                } catch (error) {
                    console.error('Failed to update join status to ended:', error);
                    // Don't fail the call end if status update fails
                }
            }

            setIsInCall(false);
            setFinalCallDuration(callDuration); // Store final duration
            setCallDuration(0);
            setRemoteUsers([]);
            setShowStartCallButton(true);
            setShowEndCallModal(false);
            setMainView({ type: 'local', remoteUid: null });
            message.success(t('agora.callEnded'));

            // Navigate back to appointment detail page
            navigate(`/appointments/${appointmentId}`);
        } catch (error) {
            console.error('Error ending call:', error);
            message.error(t('agora.callEndFailed'));
        }
    };

    const toggleAudio = async () => {
        try {
            if (localTracks.audioTrack) {
                try {
                    if (isAudioMuted) {
                        await localTracks.audioTrack.setEnabled(true);
                        // Also ensure the track is published
                        if (agoraClientRef.current) {
                            await agoraClientRef.current.publish([localTracks.audioTrack]);
                        }
                    } else {
                        await localTracks.audioTrack.setEnabled(false);
                    }
                    setIsAudioMuted(!isAudioMuted);
                    message.info(isAudioMuted ? t('agora.audioUnmuted') : t('agora.audioMuted'));
                } catch (trackError: any) {
                    // Track may be invalid - handle the error
                    console.error('Error toggling audio track:', trackError);
                    const errorMessage = trackError.message || 'Audio track is invalid. Please reconnect.';
                    setAudioDeviceError(`Audio track error: ${errorMessage}`);
                    setShowAudioDeviceDialog(true);
                    
                    // Clear the invalid track
                    setLocalTracks(prev => {
                        const newTracks = { ...prev };
                        delete newTracks.audioTrack;
                        return newTracks;
                    });
                }
            } else {
                // Try to recreate audio track if it doesn't exist
                try {
                    const audioConfig: any = {};
                    if (selectedMicrophone) {
                        audioConfig.microphoneId = selectedMicrophone;
                    }

                    const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);
                    setLocalTracks(prev => ({ ...prev, audioTrack: newAudioTrack }));

                    if (agoraClientRef.current) {
                        await agoraClientRef.current.publish([newAudioTrack]);
                    }

                    setIsAudioMuted(false);
                    message.success('Audio track recreated and enabled');
                } catch (recreateError: any) {
                    console.error('Failed to recreate audio track:', recreateError);
                    const errorMessage = `Cannot enable audio: ${recreateError.message}`;
                    setAudioDeviceError(errorMessage);
                    setShowAudioDeviceDialog(true);
                }
            }
        } catch (error: any) {
            console.error('Error toggling audio:', error);
            const errorMessage = `Audio toggle failed: ${error.message}`;
            setAudioDeviceError(errorMessage);
            setShowAudioDeviceDialog(true);
        }
    };

    // Audio volume control functions
    const setAudioVolume = async (volume: number) => {
        try {
            if (localTracks.audioTrack) {
                await localTracks.audioTrack.setVolume(volume);
            }
        } catch (error) {
            console.error('Error setting audio volume:', error);
        }
    };

    const setPlaybackVolume = async (volume: number) => {
        try {
            // Set volume for all remote audio tracks
            remoteUsers.forEach(user => {
                if (user.audioTrack) {
                    user.audioTrack.setVolume(volume);
                }
            });
        } catch (error) {
            console.error('Error setting playback volume:', error);
        }
    };

    // Set speaker for Agora remote audio tracks
    const setAgoraSpeaker = async (deviceId: string) => {
        try {
            // Set speaker for all remote audio tracks
            remoteUsers.forEach(user => {
                if (user.audioTrack) {
                    user.audioTrack.setPlaybackDevice(deviceId);
                }
            });
            console.log('Speaker set to:', deviceId);
        } catch (error) {
            console.error('Error setting Agora speaker:', error);
        }
    };

    const toggleVideo = async () => {
        try {
            if (localTracks.videoTrack) {
                if (isVideoOff) {
                    await localTracks.videoTrack.setEnabled(true);
                } else {
                    await localTracks.videoTrack.setEnabled(false);
                }
                setIsVideoOff(!isVideoOff);
                message.info(isVideoOff ? t('agora.videoOn') : t('agora.videoOff'));
            }
        } catch (error) {
            console.error('Error toggling video:', error);
            message.error(t('agora.videoToggleFailed'));
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
        if (videoRef.current) {
            if (!isFullscreen) {
                videoRef.current.requestFullscreen?.();
            } else {
                document.exitFullscreen?.();
            }
        }
    };

    const formatCallDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Click handlers to switch main view
    const handleClickLocalPiP = () => {
        if (mainView.type === 'remote') {
            lastRemoteMainUidRef.current = mainView.remoteUid ?? null;
            setMainView({ type: 'local', remoteUid: null });
        } else if (mainView.type === 'local' && lastRemoteMainUidRef.current != null) {
            setMainView({ type: 'remote', remoteUid: lastRemoteMainUidRef.current });
        }
    };

    const handleSelectRemoteAsMain = (uid: string | number) => {
        setMainView({ type: 'remote', remoteUid: uid });
        lastRemoteMainUidRef.current = uid;
    };

    if (appointmentLoading || agoraLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column'
            }}>
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>{t('agora.loadingCall')}</Text>
            </div>
        );
    }

    if (agoraError) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message={t('agora.errorTitle')}
                    description={t('agora.errorDescription')}
                    type="error"
                    showIcon
                    action={
                        <Space>
                            <Button size="small" onClick={() => window.location.reload()}>
                                {t('common.retry')}
                            </Button>
                            <Button size="small" type="primary" onClick={() => navigate(`/appointments/${appointmentId}`)}>
                                {t('common.backToAppointment')}
                            </Button>
                        </Space>
                    }
                />
            </div>
        );
    }

    if (!appointment) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message={t('agora.appointmentNotFound')}
                    description={t('agora.appointmentNotFoundDescription')}
                    type="warning"
                    showIcon
                    action={
                        <Button size="small" type="primary" onClick={() => navigate('/appointments')}>
                            {t('common.backToAppointments')}
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={FIXED_VIDEO_HEIGHT}>
            {/* Call Header */}
            <CallHeader
                appointmentId={appointment.id}
                isConnecting={isConnecting}
                isInCall={isInCall}
                callDuration={callDuration}
                networkQuality={networkQuality}
                remoteUsersCount={remoteUsers.length}
                audioDeviceError={audioDeviceError}
                localTracks={localTracks}
                isAudioMuted={isAudioMuted}
                formatCallDuration={formatCallDuration}
            />

            {/* Video Area */}
            <div style={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                minHeight: 0,
                maxHeight: 'calc(100vh - 140px)' // Account for header and controls
            }}>
                {/* Main Video */}
                <div style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden'
                }}>
                    <video
                        ref={videoRef}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            backgroundColor: '#1a1a1a'
                        }}
                        autoPlay
                        playsInline
                    />

                    {/* Start Call Button */}
                    <StartCallButton
                        showStartCallButton={showStartCallButton}
                        isInCall={isInCall}
                        isConnecting={isConnecting}
                        isTestingAudio={isTestingAudio}
                        audioDeviceError={audioDeviceError}
                        onStartCall={handleStartCall}
                        onRunAudioTest={runAudioTest}
                        onOpenMicrophoneTest={() => setShowMicrophoneTest(true)}
                    />

                    {/* Call Status Overlay */}
                    {isConnecting && !showStartCallButton && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: 'white'
                        }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                                <Text style={{ color: 'white', fontSize: 16 }}>
                                    {t('agora.connectingToCall')}
                                </Text>
                            </div>
                        </div>
                    )}

                    {/* No Remote Video Overlay */}
                    {isInCall && remoteUsers.length === 0 && !showStartCallButton && mainView.type === 'local' && !localTracks.videoTrack && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: 'white'
                        }}>
                            <UserOutlined style={{ fontSize: 64, opacity: 0.5 }} />
                            <div style={{ marginTop: 16 }}>
                                <Text style={{ color: 'white', fontSize: 16 }}>
                                    No camera detected
                                </Text>
                                <div style={{ marginTop: 8 }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                                        Waiting for participants to join
                                    </Text>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Local Video Thumbnail Window - Top Right */}
                {isInCall && localTracks.videoTrack && !isVideoOff && !minimizedThumbnails.has('local') && (
                    <div style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 128,
                        height: 96,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '2px solid rgba(255,255,255,0.2)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        cursor: 'pointer',
                        zIndex: 10
                    }} onClick={handleClickLocalPiP}>
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <video
                                ref={localVideoRef}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 8
                                }}
                                autoPlay
                                playsInline
                                muted
                            />
                            <div style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                fontSize: 12,
                                padding: '2px 8px',
                                borderRadius: 4
                            }}>
                                You
                            </div>
                        </div>
                    </div>
                )}

                {/* Placeholder for local video when video is off */}
                {isInCall && (isVideoOff || !localTracks.videoTrack) && !minimizedThumbnails.has('local') && mainView.type !== 'local' && (
                    <div style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 128,
                        height: 96,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '2px solid rgba(255,255,255,0.2)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10
                    }} onClick={handleClickLocalPiP}>
                        <div style={{ textAlign: 'center' }}>
                            <CameraOff style={{ fontSize: 32, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }} />
                            <div style={{
                                color: 'rgba(255,255,255,0.4)',
                                fontSize: 12
                            }}>
                                You
                            </div>
                        </div>
                    </div>
                )}

                {/* Remote Participants Strip (click to switch main) */}
                {isInCall && remoteUsers.length > 0 && (
                    <div style={{
                        position: 'absolute',
                        right: 16,
                        top: 128, // Position below the local video thumbnail
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        maxHeight: 'calc(100% - 200px)', // Leave space for controls and header
                        overflowY: 'hidden'
                    }}>
                        {remoteUsers.map((u, index) =>
                            !minimizedThumbnails.has(u.uid) ? (
                                <div
                                    key={String(u.uid)}
                                    onClick={() => handleSelectRemoteAsMain(u.uid)}
                                    style={{
                                        width: 128,
                                        height: u.uid !== mainView.remoteUid ? 96 : 24,
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        border: mainView.type === 'remote' && mainView.remoteUid === u.uid ? '2px solid #1890ff' : '2px solid white',
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    title={`UID: ${u.uid}`}
                                >
                                    {/* Remote thumbnail video element */}
                                    {u.videoTrack && mainView.type !== 'remote' || mainView.remoteUid !== u.uid ? (
                                        <video
                                            ref={(el) => {
                                                if (el && remoteVideoRefs.current) {
                                                    remoteVideoRefs.current[u.uid] = el;
                                                    // Play video directly into this element
                                                    if (u.videoTrack) {
                                                        try {
                                                            u.videoTrack.play(el);
                                                        } catch (e) {
                                                            // no-op; play may throw if container not attached yet
                                                        }
                                                    }
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                position: 'absolute',
                                                inset: 0
                                            }}
                                            autoPlay
                                            playsInline
                                        />
                                    ) : null}

                                    {/* Fallback avatar if no video track */}
                                    {!u.videoTrack && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: 'column' }}>
                                            <UserOutlined style={{ opacity: 0.8, fontSize: 24 }} />
                                            <span style={{ fontSize: 10 }}>
                                                {String(u.uid).startsWith('doctor') ? 'Doctor' : 'Participant'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Label */}
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 2,
                                        left: 4,
                                        right: 4,
                                        fontSize: 10,
                                        background: 'rgba(0,0,0,0.7)',
                                        padding: '2px 4px',
                                        borderRadius: 3,
                                        textAlign: 'center'
                                    }}>
                                        {/* Active indicator */}
                                        {mainView.type === 'remote' && mainView.remoteUid === u.uid && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                width: 8,
                                                height: 8,
                                                backgroundColor: '#52c41a',
                                                borderRadius: '50%',
                                                border: '1px solid white'
                                            }} />
                                        )}

                                        {String(u.uid).startsWith('doctor') ? 'Doctor' : "Patient"}
                                    </div>
                                </div>
                            ) : null
                        )}

                        {/* Placeholder for remote users without video */}
                        {remoteUsers.map((user, index) => {
                            const hasVideo = user.videoTrack;
                            if (!hasVideo && !minimizedThumbnails.has(user.uid)) {
                                return (
                                    <div
                                        key={`placeholder-${user.uid}`}
                                        onClick={() => handleSelectRemoteAsMain(user.uid)}
                                        style={{
                                            width: 120,
                                            height: 68,
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            border: mainView.type === 'remote' && mainView.remoteUid === user.uid ? '2px solid #1890ff' : '2px solid white',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            cursor: 'pointer',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            <UserOutlined style={{ fontSize: 24, opacity: 0.8, marginBottom: 4 }} />
                                            <div style={{ fontSize: 10 }}>
                                                {String(user.uid).startsWith('doctor') ? 'Doctor' : 'Participant'}
                                            </div>
                                        </div>

                                        {/* Active indicator */}
                                        {mainView.type === 'remote' && mainView.remoteUid === user.uid && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                width: 8,
                                                height: 8,
                                                backgroundColor: '#52c41a',
                                                borderRadius: '50%',
                                                border: '1px solid white'
                                            }} />
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                )}

                {/* Appointment Info Overlay */}
                <div style={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: 16,
                    borderRadius: 8,
                    color: 'white',
                    maxWidth: 300
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <Avatar icon={<UserOutlined />} size="small" />
                        <Text style={{ color: 'white', marginLeft: 8 }}>
                            {patientName || appointment.patient_phone || t('common.na')}
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                        <CalendarOutlined style={{ marginRight: 8 }} />
                        <Text style={{ color: 'white', fontSize: 12 }}>
                            {dayjs(appointment.date).format('MMM DD, YYYY')}
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ClockCircleOutlined style={{ marginRight: 8 }} />
                        <Text style={{ color: 'white', fontSize: 12 }}>
                            {appointment.time} ({appointment.duration} {t('appointments.minutes')})
                        </Text>
                    </div>

                    {appointment.appointment_notes && (
                        <div style={{ marginTop: 8 }}>
                            <Text style={{ color: 'white', fontSize: 12 }}>
                                <strong>{t('appointments.notes')}:</strong> {appointment.appointment_notes}
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            {/* Call Controls */}
            <CallControls
                isInCall={isInCall}
                isAudioMuted={isAudioMuted}
                isVideoOff={isVideoOff}
                isFullscreen={isFullscreen}
                showCaseNotes={showCaseNotes}
                onToggleAudio={toggleAudio}
                onToggleVideo={toggleVideo}
                onEndCall={handleEndCall}
                onToggleFullscreen={toggleFullscreen}
                onToggleCaseNotes={() => setShowCaseNotes(!showCaseNotes)}
                onOpenSettings={() => setShowMicrophoneTest(true)}
                onToggleDebug={() => {
                    // debugger;
                    console.log("====== remoteUsers.map(user => user.videoTrack)", remoteUsers.map(user => user.videoTrack));
                    // console.log(this)
                }}
            />

            {/* End Call Modal */}
            <Modal
                title={t('agora.endCallTitle')}
                open={showEndCallModal}
                onOk={confirmEndCall}
                onCancel={() => setShowEndCallModal(false)}
                okText={t('agora.endCall')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
            >
                <p>{t('agora.endCallConfirm')}</p>
                <p>{t('agora.callDuration', { duration: formatCallDuration(finalCallDuration) })}</p>
            </Modal>

            {/* Audio Device Detection Failed Dialog */}
            <Modal
                title={t('agora.audioDeviceDetectionFailed')}
                open={showAudioDeviceDialog}
                onCancel={() => {
                    setShowAudioDeviceDialog(false);
                    setDialogSelectedMicrophone('');
                }}
                footer={[
                    <Button 
                        key="cancel" 
                        onClick={() => {
                            setShowAudioDeviceDialog(false);
                            setDialogSelectedMicrophone('');
                        }}
                    >
                        {t('common.cancel')}
                    </Button>,
                    <Button 
                        key="reconnect" 
                        type="primary" 
                        onClick={handleReconnectAudio} 
                        loading={isReconnectingAudio}
                        disabled={!dialogSelectedMicrophone && availableMicrophones.length > 0}
                    >
                        {isReconnectingAudio ? t('agora.reconnecting') : t('agora.reconnectAudio')}
                    </Button>
                ]}
                width={500}
            >
                <div style={{ padding: '10px 0' }}>
                    <ExclamationCircleOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 16, display: 'block', textAlign: 'center' }} />
                    <p style={{ marginBottom: 16, textAlign: 'center' }}>{t('agora.audioDeviceDetectionFailedDescription')}</p>
                    
                    {audioDeviceError && (
                        <Alert
                            message={audioDeviceError}
                            type="warning"
                            showIcon
                            style={{ marginBottom: 20 }}
                        />
                    )}

                    {availableMicrophones.length > 0 ? (
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                {t('agora.microphone')}:
                            </Text>
                            <Radio.Group
                                value={dialogSelectedMicrophone}
                                onChange={(e) => setDialogSelectedMicrophone(e.target.value)}
                                style={{ width: '100%' }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {availableMicrophones.map((mic) => (
                                        <Radio key={mic.deviceId} value={mic.deviceId} style={{ display: 'block', padding: '8px 0' }}>
                                            {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                                        </Radio>
                                    ))}
                                </Space>
                            </Radio.Group>
                        </div>
                    ) : (
                        <Alert
                            message={t('agora.noMicrophoneFound')}
                            description="Please connect a microphone or click 'Refresh Devices' to detect available microphones."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Button
                        onClick={async () => {
                            setIsReconnectingAudio(true);
                            try {
                                await detectAudioDevices();
                                message.success('Devices refreshed');
                            } catch (error) {
                                message.error('Failed to refresh devices');
                            } finally {
                                setIsReconnectingAudio(false);
                            }
                        }}
                        loading={isReconnectingAudio}
                        style={{ width: '100%' }}
                    >
                        {t('agora.refreshDevices')}
                    </Button>
                </div>
            </Modal>

            {/* Audio Autoplay Blocked Dialog */}
            <Modal
                title={null}
                open={showAutoplayBlockedDialog}
                onCancel={() => setShowAutoplayBlockedDialog(false)}
                footer={[
                    <Button 
                        key="cancel" 
                        onClick={() => setShowAutoplayBlockedDialog(false)}
                    >
                        {t('common.cancel')}
                    </Button>,
                    <Button 
                        key="tryAgain" 
                        type="primary" 
                        onClick={() => {
                            setShowAutoplayBlockedDialog(false);
                            // The user interaction from clicking will allow audio to play
                            // Agora SDK will automatically retry when user interacts
                        }}
                    >
                        {t('agora.tryAgain')}
                    </Button>
                ]}
                width={400}
            >
                <div style={{ padding: '10px 0', textAlign: 'center' }}>
                    <div style={{ 
                        backgroundColor: '#fff7e6', 
                        border: '1px solid #ffd591', 
                        borderRadius: 8, 
                        padding: 16, 
                        marginBottom: 16 
                    }}>
                        <p style={{ margin: 0, color: '#d46b08', fontSize: 14 }}>
                            {t('agora.audioAutoplayBlockedInstruction')}
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Audio Test Modal */}
            <AudioTestModal
                open={showMicrophoneTest}
                onClose={() => setShowMicrophoneTest(false)}
                selectedMicrophone={selectedMicrophone}
                onMicrophoneChange={setSelectedMicrophone}
                selectedSpeaker={selectedSpeaker}
                onSpeakerChange={setSelectedSpeaker}
                availableMicrophones={availableMicrophones}
                availableSpeakers={availableSpeakers}
                onRefreshDevices={detectAudioDevices}
            />

            {/* Floating Case Notes */}
            <FloatingCaseNotes
                appointmentId={appointmentId || ''}
                initialNotes={appointment?.doctor_notes || ''}
                isVisible={showCaseNotes}
                onToggleVisibility={() => setShowCaseNotes(!showCaseNotes)}
                position={caseNotesPosition}
                onPositionChange={setCaseNotesPosition}
            />
        </div>
    );
};

export default AgoraCall; 